import { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Trophy, Award, Calendar, MapPin, Clock, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useUnits, kmToDisplay, unitLabel } from '../../lib/units';
import { formatDuration, formatEventDate } from '../../lib/formatters';

/**
 * RunBadgeRow — small inline strip of badges shown on past-event cards.
 * Two badges:
 *   • Participation — earned just by joining (always present in this list)
 *   • Completion    — earned by finishing (verified or auto-finished)
 *
 * Clicking either badge opens a dialog with the run details so we don't need
 * a separate run-detail screen yet (that arrives in Phase F).
 *
 * The FIRST time a user sees a Finished badge for an event they haven't
 * celebrated yet, we drop a one-time confetti burst (tracked per-event in
 * localStorage so it doesn't repeat on every render).
 */
const CELEBRATED_KEY = 'rivl:celebrated-badges';

function loadCelebrated() {
  try { return JSON.parse(localStorage.getItem(CELEBRATED_KEY) || '[]'); } catch { return []; }
}
function markCelebrated(eventId) {
  const list = loadCelebrated();
  if (list.includes(eventId)) return;
  list.push(eventId);
  try { localStorage.setItem(CELEBRATED_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

function fireConfetti() {
  // Two-burst pattern (left-then-right) feels more like a stadium crowd.
  const opts = { particleCount: 60, spread: 70, startVelocity: 45, gravity: 1, ticks: 200 };
  confetti({ ...opts, origin: { x: 0.2, y: 0.7 } });
  setTimeout(() => confetti({ ...opts, origin: { x: 0.8, y: 0.7 } }), 200);
}

export function RunBadgeRow({ event }) {
  const [openBadge, setOpenBadge] = useState(null); // 'participation' | 'completion' | null

  // Celebrate the very first time a Finished badge is rendered for this event.
  useEffect(() => {
    if (!event?.myCompleted || !event?.id) return;
    const celebrated = loadCelebrated();
    if (celebrated.includes(event.id)) return;
    markCelebrated(event.id);
    fireConfetti();
  }, [event?.id, event?.myCompleted]);

  return (
    <>
      <div className="flex items-center gap-2" data-testid={`badges-${event.id}`}>
        <BadgePill
          icon={Award}
          label="Joined"
          earned
          onClick={() => setOpenBadge('participation')}
          testid={`badge-participation-${event.id}`}
        />
        <BadgePill
          icon={Trophy}
          label={event.myCompleted ? 'Finished' : 'DNF'}
          earned={event.myCompleted}
          onClick={() => setOpenBadge('completion')}
          testid={`badge-completion-${event.id}`}
        />
      </div>

      <BadgeDialog
        open={openBadge != null}
        onOpenChange={() => setOpenBadge(null)}
        kind={openBadge}
        event={event}
      />
    </>
  );
}

function BadgePill({ icon: Icon, label, earned, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
      data-testid={testid}
      className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] uppercase tracking-widest font-bold transition-colors ${
        earned
          ? 'bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20'
          : 'bg-secondary/50 text-muted-foreground border border-dashed border-muted-foreground/30 hover:bg-secondary'
      }`}
    >
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

function BadgeDialog({ open, onOpenChange, kind, event }) {
  const { units } = useUnits();
  if (!event) return null;
  const isCompletion = kind === 'completion';
  const distance = kmToDisplay(event.distanceKm || 0, units);
  const finishSec = event.myFinishSec;
  const paceSec = finishSec && event.distanceKm ? Math.floor(finishSec / event.distanceKm) : null;
  const paceUnit = paceSec && units === 'mi' ? Math.round(paceSec * 1.609344) : paceSec;
  const paceLabel = paceUnit
    ? `${Math.floor(paceUnit / 60)}:${String(paceUnit % 60).padStart(2, '0')}`
    : '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid={`badge-dialog-${kind || ''}`}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={`h-12 w-12 rounded-md flex items-center justify-center ${isCompletion && event.myCompleted ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
              {isCompletion ? <Trophy className="h-6 w-6" /> : <Award className="h-6 w-6" />}
            </div>
            <div className="flex-1">
              <DialogTitle className="font-display text-xl font-bold tracking-tight">
                {isCompletion ? (event.myCompleted ? 'Finished' : 'Did not finish') : 'Joined'}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                {event.name}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="rivl-card divide-y divide-border mt-2">
          <Row icon={Calendar} k="Date" v={formatEventDate(event.date)} />
          <Row icon={MapPin} k="Location" v={event.location || '—'} />
          <Row k="Distance" v={`${distance.toFixed(units === 'mi' ? 1 : 0)} ${unitLabel(units)}`} />
          {isCompletion && event.myCompleted && (
            <>
              <Row icon={Clock} k="Finish time" v={finishSec ? formatDuration(finishSec) : '—'} />
              <Row k="Pace" v={paceSec ? `${paceLabel}/${unitLabel(units)}` : '—'} />
              <Row k="Verified" v={event.myVerified ? 'Yes' : 'Pending review'} />
            </>
          )}
          {!isCompletion && (
            <Row k="State" v={event.myState ? prettyState(event.myState) : 'Registered'} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, k, v }) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5 gap-3">
      <span className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />} {k}
      </span>
      <span className="text-sm font-medium font-mono text-right max-w-[55%] truncate">{v}</span>
    </div>
  );
}

function prettyState(s) {
  if (!s) return '';
  return s.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());
}
